import { FC } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { DuoTriangleExclamationIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    ionContent: {
        // 0px, not unitless 0: these feed Ionic's inner-scroll padding calc(), which a unitless 0 invalidates
        '--padding-top': '0px',
        '--padding-bottom': '0px',
        height: '100%',
        display: 'flex'
    },

    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px 32px 24px',
        textAlign: 'center'
    },

    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24
    },

    title: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 16
    },

    message: {
        fontSize: 14,
        marginTop: 12,
        marginBottom: 40,
        lineHeight: 1.4,
        maxWidth: 280,
        color: 'var(--ion-color-medium-shade)'
    },

    button: {
        '--padding-start': '32px',
        '--padding-end': '32px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        fontSize: 16,
        '--border-radius': '8px',
        width: '100%',
        maxWidth: 280
    }
});

interface IShortRecordingModalProps {
    isOpen: boolean;
    message: string;
    trackingWordCount: number;
    trackingDuration: number;
    onDismiss: () => void;
}

export const ShortRecordingModal: FC<IShortRecordingModalProps> = ({
    isOpen,
    message,
    trackingWordCount,
    trackingDuration,
    onDismiss
}) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const handleDismiss = () => {
        mixpanel.track('Short Recording Message Acknowledged', () => ({
            'message': message,
            'wordCount': trackingWordCount,
            'Duration Seconds': Math.round(trackingDuration / 1000)
        }));
        onDismiss();
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onDismiss}
            backdropDismiss={false}
            data-id="short-recording-modal"
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.content}>
                    <div className={classes.iconContainer}>
                        <DuoTriangleExclamationIcon color="secondary" customSize={80} />
                    </div>

                    <div className={classes.title}>
                        Short Recording Saved!
                    </div>

                    <div className={classes.message}>
                        {message}
                    </div>

                    <IonButton
                        expand="block"
                        className={classes.button}
                        onClick={handleDismiss}
                        data-id="got-it-button"
                    >
                        Got It!
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};
